import { useState, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  type ColumnDef,
} from '@tanstack/react-table';
import { Plus, Pencil, Eye, Trash2, Search, Inbox } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { useTemplates, useDeleteTemplate } from '@/hooks/use-templates';
import { usePersonNames } from '@/hooks/use-person-names';
import { useApprovalQueueCount } from '@/hooks/use-workflow';
import { REVIEW_QUEUE_STATUSES } from '@/types/workflow';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Pagination } from '@/components/ui/pagination';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/toast';
import { formatDate, statusLabel } from '@/lib/utils';
import type { Template, SurveyType, TemplateStatus, TemplateFilters } from '@/types/template';
import { canCreateTemplate, getNextStatuses } from '@/types/workflow';

const PAGE_SIZE = 25;

const STATUS_BADGE_MAP: Record<TemplateStatus, 'default' | 'secondary' | 'success' | 'warning' | 'destructive'> = {
  draft: 'secondary',
  pending_internal_review: 'default',
  tcm_admin_approved: 'default',
  pending_client_review: 'warning',
  live: 'success',
  revision_requested: 'destructive',
};

const SURVEY_TYPES: { value: SurveyType; label: string }[] = [
  { value: 'scheduled_education', label: 'Scheduled Education' },
  { value: 'biometric_triggered', label: 'Biometric Triggered' },
  { value: 'adherence', label: 'Adherence' },
  { value: 'patient_satisfaction', label: 'Patient Satisfaction' },
];

const STATUSES: { value: TemplateStatus; label: string }[] = [
  { value: 'draft', label: 'Draft' },
  { value: 'pending_internal_review', label: 'Pending Internal Review' },
  { value: 'tcm_admin_approved', label: 'TCM Admin Approved' },
  { value: 'pending_client_review', label: 'Pending Client Review' },
  { value: 'live', label: 'Live' },
  { value: 'revision_requested', label: 'Revision Requested' },
];

export default function TemplateList() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  // Read filters from URL
  const filters: TemplateFilters = useMemo(() => ({
    type: (searchParams.get('type') as SurveyType) || undefined,
    status: (searchParams.get('status') as TemplateStatus) || undefined,
    keyword: searchParams.get('keyword') || undefined,
    // SE-API-18: page/pageSize (page is 1-based).
    page: Number(searchParams.get('page') || 1),
    pageSize: PAGE_SIZE,
  }), [searchParams]);

  const currentPage = Number(searchParams.get('page') || 1);

  const { data, isLoading, error } = useTemplates(filters);
  const deleteMutation = useDeleteTemplate();

  const authorIds = useMemo(
    () => (data?.entries ?? []).map((t) => String(t.created_by)),
    [data],
  );
  const { data: personNames } = usePersonNames(authorIds);

  // Debounced search
  const [searchInput, setSearchInput] = useState(filters.keyword ?? '');
  const searchTimeoutRef = useState<ReturnType<typeof setTimeout> | null>(null);

  const updateFilter = useCallback(
    (key: string, value: string | undefined) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (value) {
          next.set(key, value);
        } else {
          next.delete(key);
        }
        next.delete('page'); // Reset to page 1 on filter change
        return next;
      });
    },
    [setSearchParams],
  );

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchInput(value);
      if (searchTimeoutRef[0]) clearTimeout(searchTimeoutRef[0]);
      searchTimeoutRef[0] = setTimeout(() => {
        updateFilter('keyword', value || undefined);
      }, 300);
    },
    [updateFilter, searchTimeoutRef],
  );

  const handlePageChange = useCallback(
    (page: number) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (page > 1) {
          next.set('page', String(page));
        } else {
          next.delete('page');
        }
        return next;
      });
    },
    [setSearchParams],
  );

  const handleDelete = useCallback(
    async (templateId: string) => {
      if (!confirm('Delete this template? This action cannot be undone.')) return;
      try {
        await deleteMutation.mutateAsync(templateId);
        toast({ title: 'Template deleted', variant: 'success' });
      } catch {
        toast({ title: 'Failed to delete template', variant: 'destructive' });
      }
    },
    [deleteMutation, toast],
  );

  const columns: ColumnDef<Template>[] = useMemo(
    () => [
      {
        accessorKey: 'title',
        header: 'Title',
        cell: ({ row }) => (
          <span className="font-medium text-carbon-900">{row.original.title}</span>
        ),
      },
      {
        accessorKey: 'type',
        header: 'Type',
        cell: ({ row }) => (
          <span className="text-sm text-carbon-600">
            {SURVEY_TYPES.find((t) => t.value === row.original.type)?.label ?? row.original.type}
          </span>
        ),
      },
      {
        accessorKey: 'disease_association',
        header: 'Disease',
        cell: ({ row }) => row.original.disease_association ?? '—',
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <Badge variant={STATUS_BADGE_MAP[row.original.status]}>
            {statusLabel(row.original.status)}
          </Badge>
        ),
      },
      {
        accessorKey: 'created_by',
        header: 'Author',
        cell: ({ row }) => {
          const name = personNames?.get(String(row.original.created_by));
          return (
            <span className="text-sm text-carbon-600">
              {name ?? row.original.created_by}
            </span>
          );
        },
      },
      {
        accessorKey: 'modified_on',
        header: 'Last Updated',
        cell: ({ row }) =>
          row.original.modified_on
            ? formatDate(row.original.modified_on)
            : formatDate(row.original.created_on),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => {
          const t = row.original;
          const role = user?.role;
          if (!role) return null;

          const nextStatuses = getNextStatuses(role, t.status);
          const canEdit = t.status === 'draft';
          const canReview = nextStatuses.length > 0;

          return (
            <div className="flex items-center gap-1 justify-end">
              {canEdit && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate(`/templates/${t.template_id}`)}
                  title="Edit"
                >
                  <Pencil className="h-4 w-4" />
                </Button>
              )}
              {!canEdit && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate(`/templates/${t.template_id}/review`)}
                  title={canReview ? 'Review' : 'View'}
                >
                  <Eye className="h-4 w-4" />
                </Button>
              )}
              {canEdit && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDelete(t.template_id)}
                  title="Delete"
                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          );
        },
      },
    ],
    [user, navigate, handleDelete, personNames],
  );

  const table = useReactTable({
    data: data?.entries ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const pageCount = data ? Math.ceil(data.total / PAGE_SIZE) : 0;

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-carbon-900">Templates</h2>
          <p className="text-sm text-carbon-500">
            Manage survey templates and their approval workflow
          </p>
        </div>
        {user && canCreateTemplate(user.role) && (
          <Button onClick={() => navigate('/templates/new')}>
            <Plus className="h-4 w-4 mr-1" />
            New Template
          </Button>
        )}
      </div>

      {/* CMS-03a — "Awaiting your review" widget. Visible only for roles
          that have a review queue (TCM Admin in first wave; Health Plan
          when CMS-03b lands). Refetches every 30s via the underlying hook. */}
      {user && (REVIEW_QUEUE_STATUSES[user.role]?.length ?? 0) > 0 && (
        <ApprovalQueueWidget />
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-carbon-400" />
          <Input
            placeholder="Search templates..."
            value={searchInput}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-9"
          />
        </div>

        <Select
          value={filters.type ?? '__none__'}
          onValueChange={(v) =>
            updateFilter('type', v === '__none__' ? undefined : v)
          }
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">All Types</SelectItem>
            {SURVEY_TYPES.map((t) => (
              <SelectItem key={t.value} value={t.value}>
                {t.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={filters.status ?? '__none__'}
          onValueChange={(v) =>
            updateFilter('status', v === '__none__' ? undefined : v)
          }
        >
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">All Statuses</SelectItem>
            {STATUSES.map((s) => (
              <SelectItem key={s.value} value={s.value}>
                {s.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {(filters.type || filters.status || filters.keyword) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSearchParams({});
              setSearchInput('');
            }}
          >
            Clear filters
          </Button>
        )}
      </div>

      {/* Table */}
      <div className="rounded-ds-008 border border-silver-200 bg-white">
        {isLoading ? (
          <div className="p-4 space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : error ? (
          <div className="p-8 text-center text-red-600">
            Failed to load templates. Check your API connection.
          </div>
        ) : (
          <>
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={columns.length} className="h-32 text-center text-carbon-500">
                      No templates found
                    </TableCell>
                  </TableRow>
                ) : (
                  table.getRowModel().rows.map((row) => (
                    <TableRow key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            {/* Pagination footer */}
            <div className="flex items-center justify-between border-t border-silver-200 px-4 py-3">
              <p className="text-sm text-carbon-500">
                {data?.total
                  ? `Showing ${(currentPage - 1) * PAGE_SIZE + 1}–${Math.min(currentPage * PAGE_SIZE, data.total)} of ${data.total}`
                  : 'No results'}
              </p>
              <Pagination
                page={currentPage}
                pageCount={pageCount}
                onPageChange={handlePageChange}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CMS-03a — "Awaiting your review" widget for the Dashboard top
// ---------------------------------------------------------------------------
// Compact card; click-through routes to the full ApprovalQueue page. The
// underlying useApprovalQueueCount() hook polls every 30s.

function ApprovalQueueWidget() {
  const { user } = useAuth();
  const { count, isLoading } = useApprovalQueueCount(user?.role);

  if (!user) return null;

  return (
    <Link
      to="/approvals"
      className="block rounded-ds-008 border border-purple-200 bg-purple-50/60 px-5 py-4 transition-colors hover:bg-purple-50"
    >
      <div className="flex items-center gap-4">
        <div className="flex h-10 w-10 items-center justify-center rounded-ds-008 bg-purple-500 text-white">
          <Inbox className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-semibold text-carbon-900">
            {isLoading
              ? 'Checking your review queue…'
              : count === 0
                ? 'No templates currently need your review'
                : `${count} template${count === 1 ? '' : 's'} awaiting your review`}
          </p>
          <p className="mt-0.5 text-xs text-carbon-500">
            Click to open the approval queue. Refreshes every 30 seconds.
          </p>
        </div>
        <span className="text-xs font-medium text-purple-600">Open →</span>
      </div>
    </Link>
  );
}
